// A reference photo's bytes, sent raw (migration 034): `POST /spec-parameters/{id}/photos?caption=`.
//
// lib/api.ts sends JSON only, so this does what the dock tablet's queue does for its photos
// (offline/iirQueue.ts): the bearer token, the image's own Content-Type, the Blob as the body (the
// browser sets Content-Length, which the route requires before it reads a byte). A refusal is
// thrown with the server's own sentence in `body.error`, which `serverMessage` reads, so the screen
// shows "This check already has 6 reference photos. Retire one first." rather than a status code.
import { apiUrl } from '../../config/api';
import { acquireApiToken, AUTH_BYPASS } from '../../config/auth';

export class PhotoUploadError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: { error: string; reason: string | null },
  ) {
    super(message);
    this.name = 'PhotoUploadError';
  }
}

export async function uploadReferencePhoto(parameterId: number, photo: Blob, caption: string): Promise<{ photoId: number; duplicate: boolean }> {
  const headers = new Headers({ 'Content-Type': photo.type || 'image/jpeg' });
  if (!AUTH_BYPASS) {
    try {
      headers.set('Authorization', `Bearer ${await acquireApiToken()}`);
    } catch (err) {
      const m = err instanceof Error ? err.message : 'Could not acquire an access token.';
      throw new PhotoUploadError(m, 401, { error: m, reason: 'token_unavailable' });
    }
  }
  const q = caption.trim() ? `?${new URLSearchParams({ caption: caption.trim() })}` : '';
  let res: Response;
  try {
    res = await fetch(apiUrl(`/spec-parameters/${parameterId}/photos${q}`), { method: 'POST', headers, body: photo });
  } catch {
    const m = 'No connection to the Quality API. The photo was not sent.';
    throw new PhotoUploadError(m, 0, { error: m, reason: 'network' });
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    const m = typeof body?.error === 'string' && body.error.trim() ? body.error : `${res.status} ${res.statusText || 'The photo was not kept.'}`;
    throw new PhotoUploadError(m, res.status, { error: m, reason: typeof body?.reason === 'string' ? body.reason : null });
  }
  return { photoId: Number(body?.photoId), duplicate: body?.duplicate === true };
}
